import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AssigneeUserSearch } from '@/components/sales/assignee-user-search'
import { SALES_FOLLOWUP_ACTIVITY_LABELS } from '@/lib/sales-followups'
import type { CreateFollowupPayload } from '@/services/sales'

type FollowupFormState = Omit<CreateFollowupPayload, 'service_instance_id' | 'enquiry_id' | 'parent_followup_id'>

type FollowupFormDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  subtitle?: string
  serviceInstanceId: string | null
  form: FollowupFormState
  onFormChange: (next: FollowupFormState) => void
  error?: string
  submitting?: boolean
  submitLabel?: string
  onSubmit: () => void
}

export function FollowupFormDialog({
  open,
  onOpenChange,
  title,
  subtitle,
  serviceInstanceId,
  form,
  onFormChange,
  error,
  submitting = false,
  submitLabel = 'Save',
  onSubmit,
}: FollowupFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {title}
            {subtitle ? <span className="ml-2 text-sm font-normal text-muted-foreground">{subtitle}</span> : null}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="followup-title">Title *</Label>
            <Input
              id="followup-title"
              value={form.title}
              onChange={(event) => onFormChange({ ...form, title: event.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Activity Type</Label>
              <Select value={form.activity_type} onValueChange={(value) => onFormChange({ ...form, activity_type: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(SALES_FOLLOWUP_ACTIVITY_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select
                value={form.priority}
                onValueChange={(value: 'low' | 'medium' | 'high') => onFormChange({ ...form, priority: value })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="followup-due">Due Date / Time</Label>
            <Input
              id="followup-due"
              type="datetime-local"
              value={form.due_at ?? ''}
              onChange={(event) => onFormChange({ ...form, due_at: event.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="followup-assignee">Assignee</Label>
            <AssigneeUserSearch
              serviceInstanceId={serviceInstanceId}
              value={form.assignee_name ?? ''}
              onSelect={(userId, name, email) => onFormChange({
                ...form,
                assignee_user_id: userId,
                assignee_name: name,
                assignee_email: email,
              })}
              onClear={() => onFormChange({
                ...form,
                assignee_user_id: null,
                assignee_name: '',
                assignee_email: '',
              })}
            />
          </div>
          <div>
            <Label htmlFor="followup-notes">Notes</Label>
            <Textarea
              id="followup-notes"
              rows={4}
              value={form.notes}
              onChange={(event) => onFormChange({ ...form, notes: event.target.value })}
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export type { FollowupFormState }
